import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { clientDownloadUrl, defer, firstDateOfMonth, lastDateOfMonth, mapById } from '/imports/utils';
import { ErrorCode } from '/imports/errors';
import { Loading } from '/imports/ui/Loading';
import { Input } from '/imports/ui/Input';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { Catalog } from '/imports/ui/Catalog';
import { Table } from '/imports/ui/Table';
import { Select } from '/imports/ui/Select';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { Checkbox } from '/imports/ui/Checkbox';
import { DateInput } from '/imports/ui/DateInput';
import { Button } from '/imports/ui/Button';
import { modelClass as productionMeterClass, ProductionMeter, productionMeterType } from '/imports/api/productionMeters';
import { ConsumptionMeter, modelClass as consumptionMeterClass } from '/imports/api/consumptionMeters';
import { UtilityAccount, modelClass as utilityAccountClass } from '/imports/api/utilityAccounts';
import { createEnergyExport } from '/imports/api/energyExport';
import { useHasConsumptionMeters, useHasProductionMeters, useMethod, useUser } from '/imports/hooks';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';

const EnergyExportPageStyled = styled(Page)`
  .page-body {
    display: flex;
    flex-direction: row;
    gap: 1.5rem;
    width: 100%;
    box-sizing: border-box;
    .catalog {
      flex: 1;
    }
  }
  .inputs {
    display: flex;
    gap: 1rem;
    button.btn {
      margin-left: 0;
    }
  }
  input.filter[type=checkbox] {
    width: 1.2rem;
    height: 1.2rem;
    margin: auto;
  }
`;

/** Page to select consumption/production meters and export their energy data over a date range. */
export const EnergyExportPage = () => {
  const user = useUser();
  const canReadConsumption = useHasConsumptionMeters();
  const canReadProduction = useHasProductionMeters();
  const [consumptionMeterSearch, setConsumptionMeterSearch] = useState<string>();
  const [productionMeterSearch, setProductionMeterSearch] = useState<string>();
  const [utilityFilter, setUtilityFilter] = useState('');
  const [consumptionMeterIds, setConsumptionMeterIds] = useState<string[]>([]);
  const [productionMeterIds, setProductionMeterIds] = useState<string[]>([]);
  const [resolution, setResolution] = useState('15min');
  const [startDate, setStartDate] = useState<string | undefined>(firstDateOfMonth().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState<string | undefined>(lastDateOfMonth().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [productionMetersResult, loadingProductionMeters] = useMethod(productionMeterClass.listMethod, { limit: 100000 }, []);
  const [consumptionMetersResult, loadingConsumptionMeters] = useMethod(consumptionMeterClass.listMethod, { limit: 100000 }, []);
  const [utilityAccountsResult, loadingUtilityAccounts] = useMethod(utilityAccountClass.listMethod, {}, []);
  const allProductionMeters: ProductionMeter[] = (productionMetersResult?.documents || []).filter((m: ProductionMeter) => m.active);
  const allConsumptionMeters: ConsumptionMeter[] = (consumptionMetersResult?.documents || []).filter((m: ConsumptionMeter) => m.active);
  const utilityAccounts: UtilityAccount[] = (utilityAccountsResult?.documents || []).filter((u: UtilityAccount) => u.active);
  const utilityAccountMap = mapById(utilityAccounts);
  const initializing = loadingProductionMeters || loadingConsumptionMeters || loadingUtilityAccounts;
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    !initializing && defer(() => setInitialized(true));
  }, [initializing]);
  /**
   * Filters a consumption meter by the selected utility account.
   * @param consumptionMeter - meter to test
   * @returns true if it matches the active utility filter
   */
  const consumptionMeterUtilityFilter = (consumptionMeter: ConsumptionMeter) => {
    if (!utilityFilter) return true;
    return consumptionMeter.utilityAccountId === utilityFilter;
  };
  /**
   * Filters a consumption meter by the search term against its name or identifier.
   * @param consumptionMeter - meter to test
   * @returns true if it matches the search term
   */
  const consumptionMeterSearchFilter = (consumptionMeter: ConsumptionMeter) => {
    const _search = consumptionMeterSearch?.toLowerCase().trim();
    if (!_search) return true;
    return consumptionMeter.name?.toLowerCase().includes(_search) || consumptionMeter.identifier?.includes(_search);
  };
  /**
   * Filters a production meter by the search term against its name or identifier.
   * @param productionMeter - meter to test
   * @returns true if it matches the search term
   */
  const productionMeterSearchFilter = (productionMeter: ProductionMeter) => {
    const _search = productionMeterSearch?.toLowerCase().trim();
    if (!_search) return true;
    return productionMeter.name?.toLowerCase().includes(_search) || productionMeter.identifier?.includes(_search);
  };
  /** Requests an energy export for the selected meters and range, then downloads it. */
  const exportData = async () => {
    setLoading(true);
    try {
      if (!startDate || !endDate) return;
      const url = await createEnergyExport.call({ startDate, endDate, consumptionMeterIds, productionMeterIds, resolution });
      clientDownloadUrl(url);
    }
    catch (error: any) {
      if (error.error === ErrorCode.notFound) {
        return toast.error('No data for the selected date range and meters');
      }
      console.log(error);
      toast.error(`${error}`);
    }
    finally {
      setLoading(false);
    }
  };
  // Both roles, as in the Nav: the export joins consumption to production, so either on its own is short.
  if (user && !(canReadConsumption && canReadProduction)) return <NotPermittedPage />;
  if (initializing) return <LoadingFlex />;
  const _consumptionMeters = allConsumptionMeters.filter(consumptionMeterUtilityFilter).filter(consumptionMeterSearchFilter);
  const _productionMeters = allProductionMeters.filter(productionMeterSearchFilter);
  return (
    <EnergyExportPageStyled className={`page${!initialized ? ' initializing' : ''}`}>
      <PageHeading
        left={<h1>Energy Export</h1>}
        right={
          <div className="inputs">
            <Select
              value={resolution}
              onChange={(resolution) => setResolution(resolution)}
              options={[
                { value: '15min', label: '15 Minutes' },
                { value: 'hourly', label: 'Hourly' },
                { value: 'daily', label: 'Daily' },
                { value: 'monthly', label: 'Monthly' },
              ]}
            />
            <DateInput value={startDate} onChange={(startDate) => setStartDate(startDate)} />
            <DateInput value={endDate} onChange={(endDate) => setEndDate(endDate)} />
            <Button onClick={() => exportData()}>{loading ? <Loading /> : 'Export'}</Button>
          </div>
        }
      />
      <PageBody>
        <Catalog>
          <h2>{strings.ConsumptionMeters()}</h2>
          <div className="catalog-tools">
            <Input onChange={(search) => setConsumptionMeterSearch(search)} placeholder={strings.Search()} value={consumptionMeterSearch} />
            <div className="filters">
              <span className="filters-label">{strings.Filters()}:</span>
              <Select
                className="filter"
                value={utilityFilter}
                options={[
                  { value: '', label: strings.All() },
                  ...utilityAccounts.map((u) => ({ value: u._id, label: u.name || u._id }))
                ]}
                onChange={(utilityFilter) => setUtilityFilter(utilityFilter)}
              />
            </div>
          </div>
          <Table>
            <table>
              <thead>
                <tr>
                  <th>
                    <Checkbox
                      onChange={(checked) => {
                        setConsumptionMeterIds(checked ? _consumptionMeters.map(c => c._id) : []);
                      }}
                    />
                  </th>
                  <th>{strings.Name()}</th>
                  <th>{strings.UtilityAccount()}</th>
                </tr>
              </thead>
              <tbody>
                {
                  _consumptionMeters.map((consumptionMeter) => (
                    <tr key={consumptionMeter._id}>
                      <td>
                        <Checkbox
                          checked={consumptionMeterIds.includes(consumptionMeter._id)}
                          onChange={(checked) => {
                            if (checked) setConsumptionMeterIds([...consumptionMeterIds, consumptionMeter._id]);
                            else setConsumptionMeterIds(consumptionMeterIds.filter((_id) => _id !== consumptionMeter._id));
                          }}
                        />
                      </td>
                      <td><Link to={`/consumption-meters/${consumptionMeter._id}`}>{consumptionMeter.name}</Link></td>
                      <td>{utilityAccountMap[consumptionMeter.utilityAccountId || '']?.name}</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </Table>
        </Catalog>
        <Catalog>
          <h2>{strings.ProductionMeters()}</h2>
          <div className="catalog-tools">
            <Input onChange={(search) => setProductionMeterSearch(search)} placeholder={strings.Search()} value={productionMeterSearch} />
          </div>
          <Table>
            <table>
              <thead>
                <tr>
                  <th>
                    <Checkbox
                      onChange={(checked) => {
                        setProductionMeterIds(checked ? _productionMeters.map(c => c._id) : []);
                      }}
                    />
                  </th>
                  <th>{strings.Name()}</th>
                  <th>{strings.Type()}</th>
                </tr>
              </thead>
              <tbody>
                {
                  _productionMeters.map((productionMeter) => (
                    <tr key={productionMeter._id}>
                      <td>
                        <Checkbox
                          checked={productionMeterIds.includes(productionMeter._id)}
                          onChange={(checked) => {
                            if (checked) setProductionMeterIds([...productionMeterIds, productionMeter._id]);
                            else setProductionMeterIds(productionMeterIds.filter((_id) => _id !== productionMeter._id));
                          }}
                        />
                      </td>
                      <td><Link to={`/production-meters/${productionMeter._id}`}>{productionMeter.name}</Link></td>
                      <td>{productionMeterType(productionMeter)}</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </Table>
        </Catalog>
      </PageBody>
    </EnergyExportPageStyled>
  );
};
